import {
  EventType,
  PeakEvent,
  eventLastDay,
} from "../../types/event";
import { getLocalDateString } from "./scheduleDateUtils";

/**
 * Presentational view-model for an event on the Schedule (Events.md §4).
 * Events store `YYYYMMDD`; the schedule views key days by `YYYY-MM-DD`, so the
 * range is expanded here (max 31 days).
 */
export interface EventVM {
  id: string;
  name: string;
  type: EventType;
  /** YYYYMMDD first / last day. */
  date: string;
  endDate: string;
  /** Every day of the event as YYYY-MM-DD. */
  dayKeys: string[];
  isMultiDay: boolean;
  /** Swatch colour, by event type (matches the type badge). */
  color: string;
  notes?: string;
  coachIssued: boolean;
  event: PeakEvent;
}

/** Swatch colour per event type — mirrors PerformanceIntentBadge's tones. */
export const EVENT_COLORS: Record<EventType, string> = {
  competition: "#0096c7", // brand / primary
  gameDay: "#2094f3", // info
  testing: "#9ca3af", // neutral
};

export const eventColorForType = (type: EventType | undefined): string =>
  (type && EVENT_COLORS[type]) || EVENT_COLORS.testing;

const yyyymmddToDate = (id: string): Date | null => {
  if (!id || id.length !== 8) return null;
  const d = new Date(
    Number(id.slice(0, 4)),
    Number(id.slice(4, 6)) - 1,
    Number(id.slice(6, 8)),
  );
  return isNaN(d.getTime()) ? null : d;
};

/** YYYY-MM-DD → YYYYMMDD. */
export const dateKeyToDateID = (key: string): string => key.replace(/-/g, "");

export const buildEventVM = (event: PeakEvent): EventVM | null => {
  const start = yyyymmddToDate(event.date);
  if (!start) return null;
  const endDate = eventLastDay(event);
  const end = yyyymmddToDate(endDate) ?? start;
  const dayKeys: string[] = [];
  const cursor = new Date(start);
  // Guard against malformed ranges (cap at 31 days).
  for (let i = 0; i < 31 && cursor <= end; i++) {
    dayKeys.push(getLocalDateString(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return {
    id: event.id,
    name: event.name || "Untitled event",
    type: event.type,
    date: event.date,
    endDate,
    dayKeys,
    isMultiDay: dayKeys.length > 1,
    color: eventColorForType(event.type),
    notes: event.notes,
    coachIssued: !!event.coachIssued,
    event,
  };
};

const toEventVMs = (events: PeakEvent[]): EventVM[] =>
  events
    .map(buildEventVM)
    .filter((vm): vm is EventVM => vm !== null)
    .sort((a, b) => a.date.localeCompare(b.date));

/** Active events for the calendar. Withdrawn events are omitted (Events.md §1.1). */
export const buildEventVMs = (events: PeakEvent[] | undefined): EventVM[] =>
  toEventVMs((events ?? []).filter((e) => e.status !== "withdrawn"));

/**
 * Withdrawn events, kept off the calendar but still openable. Reinstate and
 * Delete live on the edit sheet, which is only reached from an event row.
 */
export const buildWithdrawnEventVMs = (events: PeakEvent[] | undefined): EventVM[] =>
  toEventVMs((events ?? []).filter((e) => e.status === "withdrawn"));

/** YYYY-MM-DD → events on that day (multi-day ones appear on each). */
export const eventsByDateKey = (vms: EventVM[]): Map<string, EventVM[]> => {
  const map = new Map<string, EventVM[]>();
  for (const vm of vms) {
    for (const key of vm.dayKeys) {
      const list = map.get(key) ?? [];
      list.push(vm);
      map.set(key, list);
    }
  }
  return map;
};

/**
 * The day a performance logged from this event is filed under (YYYYMMDD):
 * today when inside the range, else the last day once it has passed, else the
 * first day.
 */
export const logDateForEvent = (
  event: Pick<PeakEvent, "date" | "endDate">,
  today: string = getLocalDateString(new Date()).replace(/-/g, ""),
): string => {
  const last = eventLastDay(event);
  if (today >= event.date && today <= last) return today;
  return today > last ? last : event.date;
};

/** 1-based day index of `dateKey` within a multi-day event, else null. */
export const eventDayIndexForKey = (
  vm: Pick<EventVM, "isMultiDay" | "dayKeys">,
  dateKey: string,
): number | null => {
  if (!vm.isMultiDay) return null;
  const index = vm.dayKeys.indexOf(dateKey);
  return index >= 0 ? index + 1 : null;
};
